import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EventCard3D } from "@/components/events/EventCard3D";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { XSpacesSection } from "./XSpacesSection";
import { SocialGroups } from "./SocialGroups";
import { SectionBannerAds } from "@/components/ads/SectionBannerAds";

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const calculateCountdown = (dateString: string | null): CountdownTime | null => {
  if (!dateString) return null;
  
  const eventDate = new Date(dateString);
  const now = new Date();
  const diff = eventDate.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, isPast: false };
};

interface Category {
  id: string;
  name_ar: string;
  name_en: string;
  icon: string;
  color: string;
  path: string;
  category_slug?: string;
  display_order: number;
  is_visible: boolean;
  events_limit: number;
}

interface DynamicCategorySectionsProps {
  isRTL: boolean;
}

// Categories that use custom sections (not scraped_events)
const CUSTOM_SECTION_PATHS = ["/x-spaces", "/social-groups"];

export const DynamicCategorySections = ({ isRTL }: DynamicCategorySectionsProps) => {
  const [countdowns, setCountdowns] = useState<Record<string, CountdownTime | null>>({});

  // Fetch all visible categories
  const { data: categories = [] } = useQuery({
    queryKey: ["visible-categories-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories" as any)
        .select("*")
        .eq("is_visible", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return (data as unknown as Category[]) || [];
    },
  });

  // Fetch all events
  const { data: allEvents = [] } = useQuery({
    queryKey: ["all-category-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scraped_events")
        .select("*")
        .eq("is_published", true)
        .order("date", { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Update countdowns - use ref to avoid infinite loop
  const eventsRef = useRef(allEvents);
  eventsRef.current = allEvents;

  useEffect(() => {
    const updateCountdowns = () => {
      const events = eventsRef.current;
      if (events.length === 0) return;
      const newCountdowns: Record<string, CountdownTime | null> = {};
      events.forEach((event) => {
        newCountdowns[event.id] = calculateCountdown(event.date);
      });
      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 60000); // Update every minute instead of every second
    return () => clearInterval(interval);
  }, [allEvents.length]); // Only re-run when the number of events changes

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Calendar;
  };

  // Get category slug from path
  const getCategorySlug = (path: string) => {
    // Handle /category/slug format
    if (path.startsWith("/category/")) {
      return path.replace("/category/", "");
    }
    // Handle /slug format
    return path.replace("/", "");
  };

  // Filter events by category with dynamic limit
  const getEventsForCategory = (category: Category) => {
    // Use category_slug if available, otherwise extract from path
    const slug = category.category_slug || getCategorySlug(category.path);
    const limit = category.events_limit || 6;
    return allEvents
      .filter(event => event.category === slug)
      .slice(0, limit);
  };

  if (categories.length === 0) return null;

  return (
    <>
      {categories.map((category) => {
        // Render custom sections for x-spaces and social-groups
        if (category.path === "/x-spaces") {
          return (
            <div key={category.id}>
              <SectionBannerAds categorySlug={category.category_slug || getCategorySlug(category.path)} />
              <XSpacesSection isRTL={isRTL} limit={category.events_limit || 6} />
            </div>
          );
        }
        
        if (category.path === "/social-groups") {
          return (
            <div key={category.id}>
              <SectionBannerAds categorySlug={category.category_slug || getCategorySlug(category.path)} />
              <SocialGroups isRTL={isRTL} />
            </div>
          );
        }

        const categoryEvents = getEventsForCategory(category);
        
        // Skip categories with no events (only for scraped_events based categories)
        if (categoryEvents.length === 0) return null;

        const IconComponent = getIconComponent(category.icon);

        return (
          <section key={category.id} className="py-4 sm:py-8">
            <SectionBannerAds categorySlug={category.category_slug || getCategorySlug(category.path)} />
            <div className="container px-3 sm:px-4">
              {/* Section Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`${category.color} p-2 sm:p-3 rounded-lg sm:rounded-xl bg-card shadow-sm border border-primary/10`}>
                    <IconComponent size={20} className="sm:hidden" />
                    <IconComponent size={28} className="hidden sm:block" />
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-secondary to-gold bg-clip-text text-transparent">
                    {isRTL ? category.name_ar : category.name_en}
                  </h2>
                </div>
                
                <Link
                  to={category.path}
                  className="flex items-center gap-1 sm:gap-2 text-primary hover:text-primary/80 transition-colors font-medium text-sm sm:text-base"
                >
                  <span>{isRTL ? "عرض الكل" : "View All"}</span>
                  {isRTL ? (
                    <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </Link>
              </div>

              {/* Events Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                {categoryEvents.map((event) => (
                  <EventCard3D
                    key={event.id}
                    event={event}
                    isRTL={isRTL}
                    countdown={countdowns[event.id] || null}
                  />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
};